import { Box, Skeleton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { THUMBNAIL_ASPECT_RATIO } from '../../../helpers/content-image'
import PromotedContentCardContent from './card-content'
import PromotedCardContainer from './container'
import usePromotedCardMethods, { PROMOTED_CARD_TYPES } from './use-promoted-card-methods'

// PENG-3630: the vertical card used for items 2-5 below the hero from md
// up. Media first (16:9, so nothing is cropped), then type/time, title and a
// 2-line summary, with the actions pinned to the bottom so they line up
// across the row.
const useStyles = makeStyles(({ spacing, typography, radius }) => ({
  root: {
    height: '100%'
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  contentContainer: {
    paddingTop: spacing(1),
    '&&': {
      // `auto`, not the base calc() or 100%: the content Box carries an 8px
      // margin, and 100% plus that margin pushes the meta row past the edge.
      width: 'auto',
      // The hover tint ends right under the actions; the 16px bottom margin
      // matches the space under the actions on the rail cards.
      paddingBottom: 0,
      marginBottom: spacing(2)
    }
  },
  // `&&` selector trick bumps CSS specificity so this override beats the
  // base `cardTitle` class from card-content.js without `!important`.
  // See generic-card.js for the same pattern.
  cardTitle: {
    '&&': {
      ...typography.heading.h5,
      fontWeight: typography.weight.extra
    }
  },
  cardSummary: {
    '&&': {
      ...typography.body.s,
      fontWeight: typography.weight.regular,
      lineClamp: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  actions: {
    '&&': {
      marginTop: 'auto',
      paddingTop: spacing(2)
    }
  },
  imageContainer: {
    aspectRatio: THUMBNAIL_ASPECT_RATIO,
    // Without these the flex item's automatic min-height wins over the
    // aspect ratio and the box grows a few px taller than 16:9.
    flex: 'none',
    minHeight: 0,
    margin: spacing(2, 2, 0, 2)
  },
  image: {
    height: '100%',
    width: '100%',
    borderRadius: radius.lg,
    objectFit: 'cover'
  }
}))

const PromotedContentSecondaryCard = ({ content, onSelect, position, testId, size, onAddToPlaylist, ...props }) => {
  const classes = useStyles()

  const {
    contentImage,
    handleCardSelect,
    expanded,
    cardSize,
    actions
  } = usePromotedCardMethods({ content, position, size, cardType: PROMOTED_CARD_TYPES.SECONDARY, onSelect, onAddToPlaylist })

  const handleImageClick = (e) => {
    e.stopPropagation() // Prevent content area from catching this
    handleCardSelect()
  }

  const handleImageKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation() // Prevent content area from catching this
      handleCardSelect()
    }
  }

  return (
    <PromotedCardContainer
      expand={expanded}
      size={cardSize}
      actions={actions}
      content={content}
      testId={testId}
    >
      <Box className={classes.layout}>
        <Box
          className={classes.imageContainer}
          onClick={handleImageClick}
          onKeyDown={handleImageKeyDown}
          role='button'
          tabIndex={0}
          aria-label={content ? `View ${content.title} image` : 'Loading content image'}
          sx={{
            cursor: 'pointer',
            '&:focus': {
              outline: '2px solid #1976d2',
              outlineOffset: '2px',
              borderRadius: '8px'
            }
          }}
        >

          {
            content
              ? (
                <img
                  src={contentImage}
                  title={content?.image?.altText}
                  className={classes.image}
                  data-test={`card-image-${content?.friendlyUrl}`}
                  alt={content?.image?.altText || ''}
                />
                )
              : (
                <Skeleton variant='rectangular' className={classes.image} />
                )
          }
        </Box>
        <PromotedContentCardContent
          content={content}
          actions={actions}
          expand={expanded}
          cardType={PROMOTED_CARD_TYPES.SECONDARY}
          onSelect={handleCardSelect}
          // Title 8px under the type pill (was 16) so the two read as one group
          defaultSectionMargin={1}
          overrides={{
            contentContainer: classes.contentContainer,
            cardTitle: classes.cardTitle,
            cardSummary: classes.cardSummary,
            actions: classes.actions
          }}
        />
      </Box>
    </PromotedCardContainer>
  )
}

export default PromotedContentSecondaryCard
